import React, { useContext, useEffect, useState, useRef } from 'react'
import { Button, Dropdown } from 'react-bootstrap'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import { useLazyQuery, useMutation } from '@apollo/client'

import styles from './style.module.scss'
import {
  GET_BOOKMARK_DETAILS,
  GET_FANTASY_ARTICLE_TOTAL_CLAP,
  GET_USER_CLAP,
  GET_USER_FANTASY_CLAP,
  GET_NEWS_ARTICLE_TOTAL_CLAP,
  UPDATE_NEWS_ARTICLE_VIEWS,
  UPDATE_FANTASY_ARTICLE_VIEWS
} from '@graphql/article/article.query'
import ToastrContext from '@shared/components/toastr/ToastrContext'
import { ADD_BOOKMARK, ADD_CLAP, ADD_FANTASY_CLAP, REMOVE_BOOKMARK } from '@graphql/article/article.mutation'
import { DOMAIN, TOAST_TYPE } from '@shared-submodule/sm-constants'
import GlobalEventsContext from '@shared/components/global-events/GlobalEventsContext'
import useModal from '@shared/hooks/useModal'
import { getToken } from '@shared/libs/token'
import { BookmarkIcon, BookmarkedIcon, HeartFillIcon, HeartIcon, ShareIconAction, CommentBubble } from '@shared/components/ctIcons'

const SocialShareList = dynamic(() => import('@shared/components/socialShare/shareList'))
const SocialAuthModal = dynamic(() => import('@shared/components/socialAuthModal'), { ssr: false })
export default function ArticleActions({ article, type, seoData, commentCount }) {
  const previewImg = `${DOMAIN}images/oneturf-Facebook-Preview.svg`
  const { dispatch } = useContext(ToastrContext)
  const [currentClap, setCurrentClap] = useState(0)
  const [clapAnimation, setClapAnimation] = useState()
  const [isNativeShare, setIsNativeShare] = useState(false)
  const [isBookmark, setIsBookmark] = useState(false)
  const bookmarkId = useRef()
  const [totalClaps, setTotalClaps] = useState(article?.nClaps)
  const { isShowing, toggle } = useModal()
  const { stateGlobalEvents, dispatchGlobalEvents: editGlobalEvent } = useContext(GlobalEventsContext)
  const token = getToken()

  useEffect(() => {
    if (token && isShowing) {
      toggle()
    }
  }, [token])

  const [getNewsArticleClap] = useLazyQuery(GET_NEWS_ARTICLE_TOTAL_CLAP, {
    fetchPolicy: 'network-only',
    variables: { input: { _id: article?._id } },
    onCompleted: data => {
      setTotalClaps(data?.getNewsArticleTotalClaps?.nTotalClap)
    }
  })

  const [getUserClap] = useLazyQuery(GET_USER_CLAP, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data && data?.getUserArticleClap) {
        setCurrentClap(data?.getUserArticleClap?.nTotalClap)
      }
    }
  })

  const [getFantasyArticleClap] = useLazyQuery(GET_FANTASY_ARTICLE_TOTAL_CLAP, {
    fetchPolicy: 'network-only',
    variables: { input: { _id: article?._id } },
    onCompleted: data => {
      setTotalClaps(data?.getFantasyArticleTotalClaps?.nTotalClap)
    }
  })

  const [getUserFantasyClap] = useLazyQuery(GET_USER_FANTASY_CLAP, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data && data?.getUserFantasyArticleClap) {
        setCurrentClap(data?.getUserFantasyArticleClap?.nTotalClap)
      }
    }
  })

  const [getBookmarkDetails] = useLazyQuery(GET_BOOKMARK_DETAILS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data && data.getBookmarks) {
        setIsBookmark(data?.getBookmarks?.bIsBookmarked)
        bookmarkId.current = data?.getBookmarks?._id
      }
    }
  })

  const [addClap] = useMutation(ADD_CLAP)
  const [addFantasyClap] = useMutation(ADD_FANTASY_CLAP)

  const [updateNewsArticleViews] = useMutation(UPDATE_NEWS_ARTICLE_VIEWS)
  const [updateFantasyArticleViews] = useMutation(UPDATE_FANTASY_ARTICLE_VIEWS)

  const [addBookmark] = useMutation(ADD_BOOKMARK, {
    onCompleted: data => {
      if (data && data.addUserBookmark) {
        bookmarkId.current = data.addUserBookmark?._id
        dispatch({
          type: 'SHOW_TOAST',
          payload: { message: data.addUserBookmark.sMessage, type: TOAST_TYPE.Success }
        })
        editGlobalEvent({
          type: 'CHANGE_PROFILE',
          payload: { profileData: { ...stateGlobalEvents.profileData, nBookmarkCount: stateGlobalEvents.profileData.nBookmarkCount + 1 } }
        })
      }
    }
  })

  const [removeBookmark] = useMutation(REMOVE_BOOKMARK, {
    onCompleted: data => {
      if (data && data.deleteBookmark) {
        dispatch({
          type: 'SHOW_TOAST',
          payload: { message: data.deleteBookmark.sMessage, type: TOAST_TYPE.Success }
        })
        editGlobalEvent({
          type: 'CHANGE_PROFILE',
          payload: { profileData: { ...stateGlobalEvents.profileData, nBookmarkCount: stateGlobalEvents.profileData.nBookmarkCount - 1 } }
        })
      }
    }
  })

  function handleClap() {
    const sToken = getToken()
    if (sToken && sToken !== 'undefined' && sToken !== 'null') {
      setClapAnimation(false)
      setTimeout(() => {
        setClapAnimation(true)
      }, 100)
      if (currentClap < 5) {
        setTotalClaps(totalClaps + 1)
        if (article?.oSeo?.eType === 'ar') {
          addClap({ variables: { input: { iArticleId: article?._id } } })
        } else {
          addFantasyClap({ variables: { input: { iArticleId: article?._id } } })
        }
        setCurrentClap(currentClap + 1)
      }
    } else {
      toggle()
    }
  }

  function handleBookmark() {
    const sToken = getToken()
    if (sToken && sToken !== 'undefined' && sToken !== 'null') {
      if (isBookmark) {
        removeBookmark({ variables: { input: { _id: bookmarkId.current } } })
        setIsBookmark(false)
      } else {
        addBookmark({ variables: { input: { eBookmarkType: article?.oSeo?.eType, iArticleId: article?._id } } })
        setIsBookmark(true)
      }
    } else {
      toggle()
    }
  }

  useEffect(() => {
    if (article?.oSeo?.eType === 'ar') {
      getNewsArticleClap()
      updateNewsArticleViews({ variables: { input: { _id: article?._id } } })
    } else {
      getFantasyArticleClap()
      updateFantasyArticleViews({ variables: { input: { _id: article?._id } } })
    }
  }, [])

  useEffect(() => {
    const t = getToken()
    if (t && t !== 'undefined' && t !== 'null') {
      if (article?.oSeo?.eType === 'ar') {
        getUserClap({ variables: { input: { iArticleId: article?._id } } })
      } else {
        getUserFantasyClap({ variables: { input: { iArticleId: article?._id } } })
      }
      getBookmarkDetails({ variables: { input: { iId: article?._id, eType: article?.oSeo?.eType } } })
    }
  }, [article])

  useEffect(() => {
    if (navigator.share) {
      setIsNativeShare(true)
    } else {
      setIsNativeShare(false)
    }
  }, [])

  async function nativeShare() {
    const url = `${DOMAIN}${seoData?.sSlug}`
    const title = seoData?.sTitle || article?.sTitle
    const postImg = article?.oImg?.sUrl ? `${DOMAIN}${article?.oImg?.sUrl}` : previewImg
    try {
      await navigator.share({
        title,
        url,
        postImg
      })
      // alert('Thanks for Sharing!')
    } catch (err) {
      // alert(`Couldn't share ${err}`)
    }
  }

  async function handleComments() {
    const scrollToElement = (await import('@shared-submodule/utils')).scrollToElement
    scrollToElement('comments', 'center')
  }

  return (
    <>
      <div
        className={`${styles.shareList} ${
          type === 'fantasyArticle' ? 'border-0 pb-0 mb-3' : 'pt-1 pb-3 mb-2 mb-md-4'
        } d-flex align-items-center justify-content-center`}>
        <Button
          variant="link"
          title={'Claps'}
          disabled={currentClap >= 1}
          className={`${styles.item} ${styles.clap} ${clapAnimation && styles.active} ${currentClap > 0 ? styles.liked : ''} ${
            !article?.oAdvanceFeature?.bAllowComments && 'me-auto'
          } d-flex justify-content-center align-items-center position-relative ${currentClap >= 1 ? 'opacity-100' : ''}`}
          onClick={handleClap}>
          <span className={`${styles.icon} d-block`}>{currentClap > 0 ? <HeartFillIcon /> : <HeartIcon />}</span>
          {totalClaps} <span className={`ms-1 ${styles.label}`}>{'Likes'}</span>
          {/* <span className={`${styles.currentClap} d-block text-center bg-secondary rounded-circle`}>
            {currentClap ? `${currentClap}` : ''}
          </span> */}
        </Button>
        {article?.oAdvanceFeature?.bAllowComments && (
          // <a
          //   href="#comments"
          //   title={"Comments"}
          //   className={`${styles.item} d-flex justify-content-center align-items-center me-auto`}
          // >
          <Button
            variant="link"
            title={'Comments'}
            className={`${styles.item} d-flex justify-content-center align-items-center me-auto`}
            onClick={() => handleComments()}>
            <span className={`${styles.icon} d-block`}>
              <CommentBubble />
            </span>
            {commentCount || article?.nCommentCount || 0} <span className={`ms-1 ${styles.label}`}>{'Comments'}</span>
          </Button>
        )}
        {isNativeShare ? (
          <Button
            variant="link"
            onClick={() => nativeShare()}
            className={`${styles.item} ${styles.share} d-flex justify-content-center align-items-center ms-auto`}>
            <span className={`${styles.icon} d-block`}>
              <ShareIconAction />
            </span>
            <span className={`ms-2 ${styles.label}`}>{'Share'}</span>
          </Button>
        ) : (
          <Dropdown>
            <Dropdown.Toggle
              variant="link"
              title={'Share'}
              id="dropdown-basic-share"
              className={`${styles.item} ${styles.share} d-flex justify-content-center align-items-center ms-auto`}>
              <span className={`${styles.icon} mt-0 d-block`}>
                <ShareIconAction />
              </span>
              <span className={`ms-2 ${styles.label}`}>{'Share'}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className={`${styles.socialShareMenu}`}>
              <SocialShareList seoData={seoData} />
            </Dropdown.Menu>
          </Dropdown>
        )}
        <Button
          title={'Bookmark'}
          variant="link"
          className={`${styles.item} ${styles.bookmark} ${
            isBookmark && styles.active
          } d-flex justify-content-center align-items-center me-0`}
          onClick={handleBookmark}>
          <span className={`${styles.icon} d-block`}>{!isBookmark ? <BookmarkIcon /> : <BookmarkedIcon />}</span>
          <span className={` ${styles.label}`}>{'Save'}</span>
        </Button>
      </div>
      {isShowing && <SocialAuthModal show={isShowing} onHide={toggle} />}
    </>
  )
}

ArticleActions.propTypes = {
  article: PropTypes.object,
  type: PropTypes.string,
  seoData: PropTypes.object,
  commentCount: PropTypes.number
}
